import { initializeLocale } from "hooks/useLocale"
import { fetchDeployedRelease } from "hooks/useNewReleaseAvailable"
import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { reloadAfterPreloadError } from "utils/preloadErrorReload"

import App from "./App"
import { initializeSentry } from "./sentry/instrument"
import * as serviceWorker from "./serviceWorker"

initializeSentry()

// Honour a `?lang=` locale before the first render, so the page never paints
// in the wrong language
initializeLocale()

/** The URL of the stylesheet Vite failed to load, when that's what the preload error is about */
const failedStylesheet = (event: VitePreloadErrorEvent): string | undefined =>
  /^Unable to preload CSS for (.+)$/.exec(event.payload.message)?.[1]

/** Loads the stylesheet again, resolving to whether it loaded this time */
const retryStylesheet = (href: string): Promise<boolean> =>
  new Promise((resolve) => {
    const link = document.createElement("link")
    link.rel = "stylesheet"
    link.href = href
    link.addEventListener("load", () => resolve(true))
    link.addEventListener("error", () => resolve(false))
    document.head.appendChild(link)
  })

// IT-2015: When we encounter `Failed to fetch dynamically imported module: <file>-COsXAhcO.js
// https://vite.dev/guide/build.html#load-error-handling
window.addEventListener("vite:preloadError", (event) => {
  const stylesheet = failedStylesheet(event)
  if (!stylesheet) {
    // The chunk is usually there and failed on a flaky connection, but a browser won't fetch a
    // module again once its import failed, so only a reload recovers. Vite still rejects the
    // import with its error, which `lazily` holds back while the page reloads.
    reloadAfterPreloadError()
    return
  }

  // A chunk works without its stylesheet, so don't let Vite throw and crash the lazy component.
  // The stylesheet is usually there (its hash rarely changes between releases) and failed on a
  // flaky connection, so retry it. Should that fail too, reload when a newer release replaced it.
  // Otherwise keep the page, as a reload costs the user whatever they were typing, but report it.
  event.preventDefault()
  void (async () => {
    if (await retryStylesheet(stylesheet)) {
      return
    }

    const release = await fetchDeployedRelease()
    const isOutdated = !!release && release !== import.meta.env.VITE_RELEASE_NAME
    if (!isOutdated || !reloadAfterPreloadError()) {
      console.error(event.payload)
    }
  })()
})

// Textbox zoom fix for iOS
if (
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  // Include ipad using desktop mode
  (/MacIntel/.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
) {
  document
    .querySelector("meta[name='viewport']")
    ?.setAttribute("content", "width=device-width, initial-scale=1, maximum-scale=1")
}

const root = createRoot(document.getElementById("root")!)

// StrictMode enables additional development behaviors (to find common bugs in
// your components). In practice, it means components will re-render an
// additional time
// https://react.dev/reference/react/StrictMode
root.render(
  <StrictMode>
    <App />
  </StrictMode>
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
